// [Review Engine V2] Deterministic clause segmentation WITH character spans, so
// every downstream finding can cite its source text (grounding, P4). Reuses the
// existing hierarchy-aware splitter and clause-name derivation, then locates each
// clause in the source to compute its [start, end) span.
import { clauseName, splitClausesWithMeta, } from "../ContractDeviationService.js";
import { classifyHeading } from "../../shared-kernel/sectionHeadings.js";
import { preambleIndexes } from "../../documentRules/readers/preamble.js";
/** Normalize HTML (or already-plain text) to plain text with clause boundaries on
 *  their own lines. Block-level tags become newlines; inline tags are dropped;
 *  common entities are decoded. Input without tags is returned essentially as-is.
 *
 *  Both segmentation entry points normalize through this FIRST — the template
 *  (typeTemplateClauses) and the draft (analyzeAgainstTemplate). Skipping it on
 *  the draft was the live bug: a CKEditor draft is HTML, so a `<h2>7. …</h2>`
 *  line starts with `<`, heading detection fails, and the whole draft collapses
 *  to the blank-line paragraph fallback (wrong boundaries, inflated clause count).
 *  segmentWithSpans itself stays a pure function of its input, so callers must
 *  normalize before calling (a test pins that: raw HTML in → 1 clause). */
export function htmlToText(input) {
    if (!/<[a-z!/][^>]*>/i.test(input))
        return input; // already plain text
    return numberListItems(input)
        .replace(/<\s*(br|hr)\s*\/?>/gi, "\n")
        .replace(/<\/\s*(p|div|li|tr|h[1-6]|section|article|blockquote|ul|ol)\s*>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&[a-z0-9#]+;/gi, " ")
        .replace(/[ \t]+/g, " ")
        .replace(/ *\n */g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
/** A numbered list's numbers exist only in how the editor draws it, so write
 *  them into the text — "2.", "2.1", "A." — the way the reader sees them; the
 *  clause segmenter finds clauses by those numbers (documentOutline renders
 *  drafts this way). Nested decimal lists compose ("2.1"); letters and romans
 *  stand alone. `<ol start="n">` continues from n. */
function numberListItems(html) {
    if (!/<ol\b/i.test(html))
        return html;
    const stack = [];
    const numbered = html.replace(/<(\/?)(ol|ul|li)\b([^>]*)>/gi, (tag, closing, name, attrs) => {
        const kind = name.toLowerCase();
        if (kind === "ol" || kind === "ul") {
            if (closing === "/")
                stack.pop();
            else {
                const start = Number(/\bstart="(\d+)"/i.exec(attrs)?.[1] ?? "1");
                stack.push(kind === "ul" ? null : { style: /list-style-type:\s*([a-z-]+)/i.exec(attrs)?.[1] ?? "decimal", n: start - 1 });
            }
            return tag;
        }
        if (closing === "/")
            return tag;
        const top = stack[stack.length - 1];
        if (top === undefined || top === null)
            return tag;
        top.n += 1;
        if (top.style !== "decimal") {
            const letter = String.fromCharCode(64 + top.n);
            const marker = top.style === "upper-latin" || top.style === "upper-alpha" ? letter : top.style === "lower-roman" ? toRoman(top.n) : letter.toLowerCase();
            return `${tag}${marker}. `;
        }
        const chain = [];
        for (let i = stack.length - 1; i >= 0 && stack[i] !== null && stack[i].style === "decimal"; i -= 1)
            chain.unshift(stack[i].n);
        return `${tag}${chain.join(".")}${chain.length === 1 ? "." : ""} `;
    });
    // A list item's paragraph already ends its line: the list tags right after it
    // must not add a blank one (the segmenter reads blank lines as boundaries).
    return numbered.replace(/<\/p>((?:\s*<\/?(?:li|ol|ul)\b[^>]*>)+)/gi, "</p>");
}
function toRoman(n) {
    const r = [[10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"]];
    let out = "";
    for (const [v, s] of r)
        while (n >= v) {
            out += s;
            n -= v;
        }
    return out;
}
/** Bump when segmentation changes what a clause is: saved template typings are
 *  keyed by it (typedTemplate.ts). */
export const SEGMENTATION_VERSION = "2026-10-19.preamble";
/**
 * [D3] Split a top-level section into its nested sub-clauses (7.1, 7.2, …) when it
 * has ≥2 sibling sub-headings that each carry substantive body — so a mixed
 * "7. Compensation" (7.1 Fixed / 7.2 Contingent) becomes two independently
 * classified, diffed, and applied clauses instead of one merged blob that could
 * hide a deviation living only in 7.2. Runs on BOTH the draft and the template
 * (same segmenter), so the two stay granularity-consistent and align-by-type
 * still pairs like with like. Returns `[section]` unchanged when the guardrails
 * don't hold, so an ordinary flat section is never fragmented.
 *
 * Guardrails against over-splitting a single-topic section:
 *  - split only at the SHALLOWEST sub-depth that RECURS (≥2 siblings);
 *  - a sub must carry a non-empty, non-heading body line to count — a bare
 *    sub-label merges FORWARD into the next sub;
 *  - require ≥2 body-bearing subs, else it's a heading list, not clauses;
 *  - the parent heading + preamble is emitted as its own clause ONLY when it has
 *    body of its own; a bare "7. Compensation" heading stays in the source
 *    document but is not made a card (the split is into 7.1 / 7.2).
 */
function splitNestedSections(sectionText) {
    const lines = sectionText.split(/\r?\n/);
    const cls = lines.map((l) => classifyHeading(l));
    const isHead = (i) => cls[i] !== null && !cls[i].isList;
    const headIdx = lines.findIndex((_, i) => isHead(i));
    if (headIdx < 0)
        return [sectionText]; // no heading → nothing to nest under
    const sectionDepth = cls[headIdx].depth;
    // Sub-heading line indices deeper than the section, grouped by depth.
    const byDepth = new Map();
    for (let i = headIdx + 1; i < lines.length; i += 1) {
        if (!isHead(i) || cls[i].depth <= sectionDepth)
            continue;
        const at = byDepth.get(cls[i].depth);
        if (at === undefined)
            byDepth.set(cls[i].depth, [i]);
        else
            at.push(i);
    }
    // Shallowest sub-depth that recurs (≥2) is the sub-boundary depth.
    let heads = null;
    for (const d of [...byDepth.keys()].sort((a, b) => a - b)) {
        if (byDepth.get(d).length >= 2) {
            heads = byDepth.get(d);
            break;
        }
    }
    if (heads === null)
        return [sectionText];
    const hasBody = (start, end) => lines.slice(start + 1, end).some((l, j) => l.trim().length > 0 && !isHead(start + 1 + j));
    const bodyBearing = heads.filter((s, k) => hasBody(s, k + 1 < heads.length ? heads[k + 1] : lines.length));
    if (bodyBearing.length < 2)
        return [sectionText]; // a heading list, not clauses
    const segs = [];
    // Parent heading + preamble → its own clause only when it carries body.
    const headHasBody = lines.slice(0, heads[0]).some((l, i) => l.trim().length > 0 && !isHead(i));
    if (headHasBody)
        segs.push(lines.slice(0, heads[0]).join("\n").trim());
    // Each sub; a bodyless sub-label merges forward into the next.
    let pending = "";
    for (let k = 0; k < heads.length; k += 1) {
        const s = heads[k];
        const e = k + 1 < heads.length ? heads[k + 1] : lines.length;
        const chunk = lines.slice(s, e).join("\n").trim();
        const merged = pending.length > 0 ? `${pending}\n${chunk}` : chunk;
        if (hasBody(s, e)) {
            segs.push(merged);
            pending = "";
        }
        else {
            pending = merged; // bare label — carry forward
        }
    }
    if (pending.length > 0 && segs.length > 0)
        segs[segs.length - 1] += `\n${pending}`;
    return segs.length >= 2 ? segs : [sectionText];
}
/** The depth of a segment's first heading line (its section level), or null. */
function segmentDepth(segment) {
    for (const l of segment.split(/\r?\n/)) {
        const c = classifyHeading(l);
        if (c !== null && !c.isList)
            return c.depth;
    }
    return null;
}
/** Is a segment ONLY heading line(s) with no body? (a bare "7. Compensation"). */
function isBareHeadingSegment(segment) {
    let sawHeading = false;
    for (const l of segment.split(/\r?\n/)) {
        if (l.trim().length === 0)
            continue;
        const c = classifyHeading(l);
        if (c !== null && !c.isList) {
            sawHeading = true;
            continue;
        }
        return false; // a body line → not a bare heading
    }
    return sawHeading;
}
/** [D3] Re-attach a bare parent-heading segment to the DEEPER segments that
 *  follow it. When a section's parent depth doesn't recur (a single "7." whose
 *  only siblings are its own 7.1/7.2), splitClausesWithMeta picks the sub-depth
 *  as the boundary and emits "7. Compensation" as its own bare segment ahead of
 *  the subs. Coalescing them back into one section lets splitNestedSections apply
 *  the SAME parent-drop / sub-merge rules it uses when the parent depth recurs —
 *  so the split is identical either way. */
function coalesceParentLeads(sections) {
    const out = [];
    let i = 0;
    while (i < sections.length) {
        const cur = sections[i];
        const d = segmentDepth(cur);
        if (isBareHeadingSegment(cur) && d !== null) {
            const group = [cur];
            let j = i + 1;
            while (j < sections.length) {
                const dj = segmentDepth(sections[j]);
                if (dj !== null && dj > d) {
                    group.push(sections[j]);
                    j += 1;
                }
                else
                    break;
            }
            if (group.length > 1) {
                out.push(group.join("\n"));
                i = j;
                continue;
            }
        }
        out.push(cur);
        i += 1;
    }
    return out;
}
/** Segment a draft into clauses, each with a char span into `source`. Clauses are
 *  contiguous slices, so we locate each verbatim from a running cursor (falling
 *  back to its first non-empty line if the exact slice was normalised away).
 *  Structured sections are further split into nested sub-clauses (D3). */
export function segmentWithSpans(source) {
    const { clauses: sections, structured } = splitClausesWithMeta(source);
    // Nested sub-clauses only exist within a STRUCTURED split (the paragraph
    // fallback has no headings to nest under). Coalesce bare parent leads first so
    // splitNestedSections sees whole sections regardless of the chosen boundary.
    const clauses = structured
        ? coalesceParentLeads(sections).flatMap((s) => splitNestedSections(s))
        : sections;
    const out = [];
    const preamble = preambleIndexes(clauses);
    let cursor = 0;
    clauses.forEach((text, index) => {
        let start = source.indexOf(text, cursor);
        if (start < 0) {
            const firstLine = text
                .split(/\r?\n/)
                .map((l) => l.trim())
                .find((l) => l.length > 0) ?? text.slice(0, 24);
            const at = source.indexOf(firstLine, cursor);
            start = at >= 0 ? at : cursor;
        }
        const end = start + text.length;
        cursor = end;
        out.push({ index, heading: clauseName(text, index), text, span: { start, end }, preamble: preamble.has(index) });
    });
    return {
        clauses: out,
        method: structured ? "structural" : "paragraph",
        confidence: structured ? 0.85 : 0.4,
    };
}
//# sourceMappingURL=segment.js.map